import { useEffect, useState } from 'react'
import { useAuthStore } from './useAuth'
import axios from 'axios'

// VAPID key de Firebase Console → Configuración del proyecto → Cloud Messaging → Clave pública del certificado web push
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? 'YOUR_VAPID_KEY'
const FCM_ENABLED = import.meta.env.VITE_FIREBASE_API_KEY && import.meta.env.VITE_FIREBASE_API_KEY !== 'YOUR_API_KEY'

export function useFCM() {
  const { token, user } = useAuthStore()
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [fcmToken, setFcmToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])

  const requestPermission = async () => {
    if (!FCM_ENABLED) {
      console.warn('[FCM] Firebase no configurado. Agrega las variables VITE_FIREBASE_* en .env')
      return false
    }
    if (!('Notification' in window)) return false
    if (!token || !user) return false

    try {
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') return false

      // Importación dinámica para no bloquear si Firebase no está configurado
      const { messaging, getToken } = await import('../lib/firebase')
      if (!messaging) return false

      const currentToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: await navigator.serviceWorker.getRegistration(),
      })

      if (currentToken) {
        setFcmToken(currentToken)
        // Guardar token en backend
        await axios.patch(
          '/api/users/me/fcm-token',
          { token: currentToken },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        console.log('[FCM] Token registrado:', currentToken.slice(0, 20) + '...')
        return true
      }
    } catch (err: any) {
      setError(err?.message ?? 'Error al activar notificaciones')
      console.error('[FCM] Error:', err)
    }
    return false
  }

  // Escuchar mensajes en foreground
  useEffect(() => {
    if (!FCM_ENABLED || !fcmToken) return

    let unsubscribe: (() => void) | null = null

    import('../lib/firebase').then(({ messaging, onMessage }) => {
      if (!messaging) return
      unsubscribe = onMessage(messaging, (payload) => {
        console.log('[FCM] Mensaje en foreground:', payload)
        // Mostrar notificación nativa si la app está abierta
        if (Notification.permission === 'granted' && payload.notification) {
          new Notification(payload.notification.title ?? 'Aqua', {
            body: payload.notification.body ?? '',
            icon: '/icon-192.png',
          })
        }
      })
    })

    return () => { unsubscribe?.() }
  }, [fcmToken])

  return { permission, fcmToken, error, requestPermission }
}
